import { useTranslation } from 'react-i18next';
import type { OneOnOne, EmployeeInfo } from '@momentum/types';
import type { DisplayEmployeeWithRole } from '../shared/types/one-on-one.types';
import { MeetingCard } from './MeetingCard';

interface ProfessionalMeetingWithEmployee {
  employeeId: string;
  employeeName: string;
  employeeImageUrl?: string;
  employeeRole: string;
  meetings: OneOnOne[];
}

interface ProfessionalMeetingsAsFacilitatorProps {
  professionalMeetings: ProfessionalMeetingWithEmployee[];
  displayEmployee: DisplayEmployeeWithRole | null;
  onSelectEmployee: (employee: DisplayEmployeeWithRole) => void;
  allEmployees: EmployeeInfo[];
}

export const ProfessionalMeetingsAsFacilitator = ({
  professionalMeetings,
  displayEmployee,
  onSelectEmployee,
  allEmployees,
}: ProfessionalMeetingsAsFacilitatorProps) => {
  const { t } = useTranslation('oneOnOne');

  if (!professionalMeetings || professionalMeetings.length === 0) {
    return null;
  }

  return (
    <>
      <div className="flex justify-center py-6">
        <div className="w-[90%] border-t border-slate-200" />
      </div>

      <div className="px-6 pb-2">
        <h3 className="mb-4 text-xs font-medium uppercase tracking-wide text-slate-500">
          {t('professionalMeetingsFacilitator')}
        </h3>
        <div className="space-y-3">
          {professionalMeetings.map((profMeeting) => {
            const isSelected = displayEmployee?._id === profMeeting.employeeId;

            const meetingsWithDates =
              profMeeting.meetings?.filter(
                (meeting) => meeting.schedualedDate
              ) || [];

            const lastMeetingDate =
              meetingsWithDates.length > 0
                ? meetingsWithDates.reduce((latest, meeting) => {
                    if (!meeting.schedualedDate) return latest;
                    const meetingDate = new Date(meeting.schedualedDate);
                    const latestDate = latest ? new Date(latest) : new Date(0);
                    return meetingDate > latestDate
                      ? meeting.schedualedDate
                      : latest;
                  }, null as string | null)
                : null;

            return (
              <MeetingCard
                key={profMeeting.employeeId}
                employeeName={profMeeting.employeeName}
                employeeImageUrl={profMeeting.employeeImageUrl}
                lastMeetingDate={lastMeetingDate}
                isSelected={isSelected}
                onSelect={() => {
                  const employee = allEmployees.find(
                    (e) => e._id === profMeeting.employeeId
                  );
                  if (employee) {
                    onSelectEmployee({
                      _id: employee._id,
                      firstName: employee.personalInfo.firstName,
                      lastName: employee.personalInfo.lastName,
                      imageUrl: employee.personalInfo.imageUrl,
                      role: employee.role,
                      managerId: employee.workInfo.managerId,
                      lastMeetingDate: lastMeetingDate || undefined,
                      daysSinceLastMeeting: undefined,
                      allMeetings: profMeeting.meetings,
                      meetingRole: 'manager',
                    });
                  }
                }}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};
